import type {ReactElement} from 'react';
import {useRef, useState} from 'react';
import React from 'react';
import type {ChatHistoryListItemProps} from './chat-history-list-item.types';
import styles from './chat-history-list-item.module.scss';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import {ChatHistoryListItemThumb} from '@/ai-chat/components/chat-history-list-item-thumb/chat-history-list-item-thumb';
import {InputField} from '@Components/input-field-v2';
import {InputFieldSize} from '@Components/input-field-v2/input-field.types';
import {Text, TextSize, TextType} from '@Components/text';
import {ChatHistoryListItemOptions} from '@/ai-chat/components/chat-history-list/components/chat-history-list-item-options/chat-history-list-item-options';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {getChatUrl} from '@/ai-chat/lib/chat-history-library';
import {deleteChat, renameChat} from '@Libraries/ai-chat.library';
import {MiddleClickNewTab} from '@Components/middle-click-new-tab';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {useDispatch, useSelector} from 'react-redux';
import {aiChatWindowActions} from '@Components/ai-chat-window/ai-chat-window.reducer';
import type {RootState} from '@/store';
import type {HistoryItem} from '@AiChat/ai-chat.types';

const AI_CHAT_TITLE_CHARACTER_LIMIT = 255;
const CHAT_TITLE_TOO_LONG_ERROR_CODE = 'title_too_long';

export function ChatHistoryListItem({...props}: ChatHistoryListItemProps): ReactElement {
  const {actions, state} = useAiChat();
  const dispatch = useDispatch();
  const cancelRenameRef = useRef(false);
  const justStartedRenamingRef = useRef(false);
  const [isMoreOptionsDropdownOpen, setIsMoreOptionsDropdownOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [renamingItemId, setRenamingItemId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const isSelected = props.historyItem.idAiChat === state.currentChatId;

  const isStreaming = useSelector((chatState: RootState) => {
    return chatState.aiChatWindow[props.historyItem.idAiChat]?.isStreaming ?? false;
  });

  const onDeleteChat = async (): Promise<void> => {
    try {
      if (isStreaming) {
        actions.disconnectChatStream(props.historyItem.idAiChat);
      }

      dispatch(aiChatWindowActions.clearChatState({chatId: props.historyItem.idAiChat}));

      await deleteChat(props.historyItem.idAiChat);

      if (isSelected) {
        actions.setCurrentChatId(undefined);
      }

      actions.deleteChatHistoryItem(props.historyItem.idAiChat);
    } catch (error) {
      showMessageGrowl({
        type: GROWL_TYPE.DANGER,
        text: window.i18next.t('pmwjs_chat_delete_error'),
        showCloseIcon: true,
        interval: 5500,
      });
    }
  };

  const onRenameSave = async (historyItem: HistoryItem, newTitle: string): Promise<void> => {
    justStartedRenamingRef.current = false;

    const trimmedNewChatTitle = newTitle.trim();

    setRenamingItemId(null);

    if (!trimmedNewChatTitle || trimmedNewChatTitle === historyItem.title) {
      return;
    }

    try {
      actions.updateChatHistoryItem(historyItem.idAiChat, {title: trimmedNewChatTitle});

      await renameChat(historyItem.idAiChat, trimmedNewChatTitle);
    } catch (error: any) {
      actions.updateChatHistoryItem(historyItem.idAiChat, {title: historyItem.title});

      showMessageGrowl({
        type: GROWL_TYPE.DANGER,
        text:
          error?.code === CHAT_TITLE_TOO_LONG_ERROR_CODE
            ? window.i18next.t('pmwjs_chat_title_too_long', {limit: AI_CHAT_TITLE_CHARACTER_LIMIT})
            : window.i18next.t('pmwjs_chat_rename_error'),
        showCloseIcon: true,
        interval: 5500,
      });
    }
  };

  const onRenameCancel = (): void => {
    justStartedRenamingRef.current = false;

    cancelRenameRef.current = true;
    setRenamingItemId(null);
  };

  const showMoreOptions = (historyItemId: string): boolean => {
    return isMoreOptionsDropdownOpen || ((isHovered || state.isSmall) && renamingItemId !== historyItemId);
  };

  const getActiveStyle = (): string => {
    return isSelected ? `bg-primary-10 ${styles.active}` : '';
  };

  const handleInputBlur = (): void => {
    if (state.isSmall && justStartedRenamingRef.current) {
      justStartedRenamingRef.current = false;
      inputRef?.current?.focus();
      return;
    }

    if (inputRef.current) {
      void onRenameSave(props.historyItem, inputRef.current.value);
    }
  };

  const getRenamingState = (): ReactElement => {
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            onRenameCancel();
            e.stopPropagation();
          }
        }}
        className={`${isSelected ? 'content-primary' : ''} ${styles.inputWrapper}`}
      >
        <InputField
          ref={inputRef}
          retainFocusOnTab
          maxLength={AI_CHAT_TITLE_CHARACTER_LIMIT}
          isControlled={false}
          autoFocus
          inputClassName={`spacing-p-l-1 ${styles.input} ${isSelected ? styles.selected : ''}`}
          input={props.historyItem.title ?? window.i18next.t('pmwjs_new_chat')}
          size={InputFieldSize.SMALL}
          onInputSubmit={() => {
            if (inputRef.current) {
              void onRenameSave(props.historyItem, inputRef.current.value);
            }
          }}
          doBlurOnSubmit={false}
          onInputBlur={handleInputBlur}
        />
      </div>
    );
  };

  const getHistoryItem = (): ReactElement => {
    return (
      <MiddleClickNewTab href={getChatUrl(props.historyItem.idAiChat)}>
        <ClickableDiv
          key={props.historyItem.idAiChat}
          onClick={() => {
            if (renamingItemId === props.historyItem.idAiChat) return;
            actions.setCurrentChatId(props.historyItem.idAiChat);
            actions.setIsSidebarExpanded(!state.isSmall);
          }}
          className={`spacing-p-1 _position-relative ${styles.sidebarItem} ${getActiveStyle()} ${renamingItemId === props.historyItem.idAiChat ? styles.renaming : ''}`}
          onPointerEnter={() => {
            setIsHovered(true);
          }}
          onPointerLeave={() => {
            setIsHovered(false);
          }}
        >
          <ChatHistoryListItemThumb key={props.historyItem.idAiChat} historyItem={props.historyItem} />

          {renamingItemId === props.historyItem.idAiChat ? (
            getRenamingState()
          ) : (
            <Text
              bold={isSelected}
              val={props.historyItem.title ?? window.i18next.t('pmwjs_new_chat')}
              title={props.historyItem.title}
              type={TextType.BODY}
              size={TextSize.XSMALL}
              className={`${isSelected ? 'content-primary' : ''} ${styles.sidebarText}`}
            />
          )}

          {showMoreOptions(props.historyItem.idAiChat) ? (
            <ChatHistoryListItemOptions
              onOpen={() => {
                setIsMoreOptionsDropdownOpen(true);
              }}
              onClose={() => {
                setIsMoreOptionsDropdownOpen(false);
              }}
              historyItem={props.historyItem}
              onStartRename={() => {
                justStartedRenamingRef.current = true;
                setRenamingItemId(props.historyItem.idAiChat);
              }}
              onDelete={() => {
                void onDeleteChat();
              }}
            />
          ) : null}
        </ClickableDiv>
      </MiddleClickNewTab>
    );
  };

  return getHistoryItem();
}
